import React, { useEffect, useState } from "react"
import {
    Row,
    Col,
    Card,
    CardBody,
    CardTitle,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    Label,
    Input,
    FormFeedback,
} from "reactstrap"
import { connect } from "react-redux"
import * as Yup from "yup"
import { useFormik } from "formik"

//Import Actions
import {
    setBreadcrumbItems,
    getProducts,
    addNewProduct,
    updateProduct,
    deleteProduct,
} from "../../store/actions"

const ProductsList = props => {
    document.title = "Produits & Services | CRM Entreprise"

    const [modal, setModal] = useState(false)
    const [isEdit, setIsEdit] = useState(false)
    const [currentProduct, setCurrentProduct] = useState(null)

    const breadcrumbItems = [
        { title: "Stocks", link: "#" },
        { title: "Produits", link: "#" },
        { title: "Catalogue", link: "#" },
    ]

    useEffect(() => {
        props.setBreadcrumbItems("Produits & Services", breadcrumbItems)
        props.getProducts()
    }, [])

    const toggle = () => {
        if (modal) {
            setModal(false)
            setCurrentProduct(null)
            setIsEdit(false)
        } else {
            setModal(true)
        }
    }

    const handleProductClick = arg => {
        const product = arg
        setCurrentProduct({
            id: product.id,
            sku: product.sku,
            name: product.name,
            description: product.description || "",
            unit_price: product.unit_price,
            cost_price: product.cost_price || "",
            tax_rate: product.tax_rate || "20.00",
            unit: product.unit || "piece",
            category: product.category || "",
            stock_quantity: product.stock_quantity || 0,
            is_active: product.is_active,
        })
        setIsEdit(true)
        toggle()
    }

    const handleDeleteProduct = product => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")) {
            props.deleteProduct(product.id)
        }
    }

    const validation = useFormik({
        enableReinitialize: true,
        initialValues: {
            sku: (currentProduct && currentProduct.sku) || "",
            name: (currentProduct && currentProduct.name) || "",
            description: (currentProduct && currentProduct.description) || "",
            unit_price: (currentProduct && currentProduct.unit_price) || "",
            cost_price: (currentProduct && currentProduct.cost_price) || "",
            tax_rate: (currentProduct && currentProduct.tax_rate) || "20.00",
            unit: (currentProduct && currentProduct.unit) || "piece",
            category: (currentProduct && currentProduct.category) || "",
            stock_quantity: (currentProduct && currentProduct.stock_quantity) || 0,
            is_active: (currentProduct && currentProduct.is_active === false) ? false : true,
        },
        validationSchema: Yup.object({
            sku: Yup.string().required("Veuillez saisir le SKU"),
            name: Yup.string().required("Veuillez saisir le nom"),
            unit_price: Yup.number().required("Veuillez saisir le prix unitaire").min(0, "Le prix doit être positif"),
            unit: Yup.string().required("Veuillez choisir l'unité"),
        }),
        onSubmit: (values) => {
            if (isEdit) {
                const updatedProduct = {
                    id: currentProduct.id,
                    ...values,
                }
                props.updateProduct(updatedProduct)
            } else {
                props.addNewProduct(values)
            }
            toggle()
        },
    })

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('fr-MA', {
            style: 'currency',
            currency: 'MAD'
        }).format(amount)
    }

    return (
        <React.Fragment>
            <div className="container-fluid">
                <Row>
                    <Col className="col-12">
                        <Card>
                            <CardBody>
                                <div className="d-flex align-items-center justify-content-between mb-4">
                                    <CardTitle className="h4 mb-0">Catalogue Produits & Services</CardTitle>
                                    <Button color="primary" onClick={toggle}>
                                        <i className="mdi mdi-plus me-1"></i> Ajouter un Produit
                                    </Button>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-hover table-centered align-middle table-nowrap mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th className="ps-4" style={{ width: "10%" }}>SKU</th>
                                                <th style={{ width: "25%" }}>Nom du Produit</th>
                                                <th style={{ width: "20%" }}>Catégorie</th>
                                                <th className="text-end" style={{ width: "15%" }}>Prix Unitaire</th>
                                                <th className="text-center" style={{ width: "10%" }}>TVA</th>
                                                <th className="text-center" style={{ width: "10%" }}>Stock</th>
                                                <th className="text-center" style={{ width: "10%" }}>Statut</th>
                                                <th className="text-center" style={{ width: "10%" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {props.products && props.products.length > 0 ? (
                                                props.products.map((product, key) => (
                                                    <tr key={key}>
                                                        <td className="ps-4">
                                                            <span className="badge bg-light text-body p-2 px-3 fw-semibold border">
                                                                {product.sku}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <h6 className="text-truncate mb-0" style={{ maxWidth: "250px" }}>
                                                                {product.name}
                                                            </h6>
                                                        </td>
                                                        <td>
                                                            <span className="text-muted">
                                                                <i className="mdi mdi-tag-outline me-1"></i>
                                                                {product.category || "Standard"}
                                                            </span>
                                                        </td>
                                                        <td className="text-end">
                                                            <span className="fw-bold text-primary">
                                                                {formatCurrency(product.unit_price)}
                                                            </span>
                                                        </td>
                                                        <td className="text-center text-muted">
                                                            {product.tax_rate}%
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge className={product.stock_quantity > 0 ? "bg-success-subtle text-success" : "bg-danger-subtle text-danger"}>
                                                                {product.stock_quantity} {product.unit}
                                                            </Badge>
                                                        </td>
                                                        <td className="text-center">
                                                            <Badge className={product.is_active ? "bg-success text-white" : "bg-danger text-white"}>
                                                                {product.is_active ? "Actif" : "Inactif"}
                                                            </Badge>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex gap-2">
                                                                <Button
                                                                    color="light"
                                                                    size="sm"
                                                                    className="text-primary"
                                                                    onClick={() => handleProductClick(product)}
                                                                >
                                                                    <i className="mdi mdi-pencil font-size-14"></i>
                                                                </Button>
                                                                <Button
                                                                    color="light"
                                                                    size="sm"
                                                                    className="text-danger"
                                                                    onClick={() => handleDeleteProduct(product)}
                                                                >
                                                                    <i className="mdi mdi-delete font-size-14"></i>
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="8" className="text-center py-4">
                                                        <i className="mdi mdi-package-variant font-size-24 d-block mb-2 text-muted"></i>
                                                        Aucun produit trouvé
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Product Modal */}
                <Modal isOpen={modal} toggle={toggle} size="lg">
                    <ModalHeader toggle={toggle}>
                        {isEdit ? "Modifier le Produit" : "Ajouter un Produit"}
                    </ModalHeader>
                    <ModalBody>
                        <Form onSubmit={validation.handleSubmit}>
                            <Row>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <Label>SKU <span className="text-danger">*</span></Label>
                                        <Input
                                            name="sku"
                                            type="text"
                                            placeholder="Ex: PRD-001"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.sku}
                                            invalid={validation.touched.sku && validation.errors.sku}
                                        />
                                        {validation.touched.sku && validation.errors.sku && (
                                            <FormFeedback type="invalid">{validation.errors.sku}</FormFeedback>
                                        )}
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="mb-3">
                                        <Label>Nom <span className="text-danger">*</span></Label>
                                        <Input
                                            name="name"
                                            type="text"
                                            placeholder="Nom du produit/service"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.name}
                                            invalid={validation.touched.name && validation.errors.name}
                                        />
                                        {validation.touched.name && validation.errors.name && (
                                            <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
                                        )}
                                    </div>
                                </Col>
                            </Row>

                            <div className="mb-3">
                                <Label>Description</Label>
                                <Input
                                    name="description"
                                    type="textarea"
                                    rows="3"
                                    placeholder="Description détaillée..."
                                    onChange={validation.handleChange}
                                    value={validation.values.description}
                                />
                            </div>

                            <Row>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <Label>Prix Unitaire (H.T) <span className="text-danger">*</span></Label>
                                        <Input
                                            name="unit_price"
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            onChange={validation.handleChange}
                                            onBlur={validation.handleBlur}
                                            value={validation.values.unit_price}
                                            invalid={validation.touched.unit_price && validation.errors.unit_price}
                                        />
                                        {validation.touched.unit_price && validation.errors.unit_price && (
                                            <FormFeedback type="invalid">{validation.errors.unit_price}</FormFeedback>
                                        )}
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <Label>Coût d'achat (Optionnel)</Label>
                                        <Input
                                            name="cost_price"
                                            type="number"
                                            step="0.01"
                                            placeholder="0.00"
                                            onChange={validation.handleChange}
                                            value={validation.values.cost_price}
                                        />
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <Label>Taux TVA (%)</Label>
                                        <Input
                                            name="tax_rate"
                                            type="select"
                                            onChange={validation.handleChange}
                                            value={validation.values.tax_rate}
                                        >
                                            <option value="0.00">0%</option>
                                            <option value="7.00">7%</option>
                                            <option value="10.00">10%</option>
                                            <option value="14.00">14%</option>
                                            <option value="20.00">20%</option>
                                        </Input>
                                    </div>
                                </Col>
                            </Row>

                            <Row>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <Label>Unité <span className="text-danger">*</span></Label>
                                        <Input
                                            name="unit"
                                            type="select"
                                            onChange={validation.handleChange}
                                            value={validation.values.unit}
                                        >
                                            <option value="piece">Pièce(s)</option>
                                            <option value="service">Service/Prestation</option>
                                            <option value="hour">Heure(s)</option>
                                            <option value="day">Jour(s)</option>
                                            <option value="kg">kilogramme (kg)</option>
                                            <option value="meter">Mètre (m)</option>
                                        </Input>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <Label>Catégorie</Label>
                                        <Input
                                            name="category"
                                            type="text"
                                            placeholder="Ex: Matériel, Service..."
                                            onChange={validation.handleChange}
                                            value={validation.values.category}
                                        />
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="mb-3">
                                        <Label>Stock Initial</Label>
                                        <Input
                                            name="stock_quantity"
                                            type="number"
                                            onChange={validation.handleChange}
                                            value={validation.values.stock_quantity}
                                        />
                                    </div>
                                </Col>
                            </Row>

                            <div className="mb-3">
                                <Label>Statut</Label>
                                <div className="form-check form-switch">
                                    <Input
                                        className="form-check-input"
                                        type="checkbox"
                                        id="is_active"
                                        name="is_active"
                                        checked={validation.values.is_active}
                                        onChange={validation.handleChange}
                                    />
                                    <Label className="form-check-label" htmlFor="is_active">
                                        Produit Actif
                                    </Label>
                                </div>
                            </div>

                            <div className="text-end mt-4">
                                <Button type="button" color="secondary" className="me-2" onClick={toggle}>
                                    Annuler
                                </Button>
                                <Button type="submit" color="primary">
                                    {isEdit ? "Mettre à jour" : "Ajouter le produit"}
                                </Button>
                            </div>
                        </Form>
                    </ModalBody>
                </Modal>
            </div>
        </React.Fragment>
    )
}

const mapStateToProps = state => ({
    products: state.Products.products,
    loading: state.Products.loading,
})

export default connect(mapStateToProps, {
    setBreadcrumbItems,
    getProducts,
    addNewProduct,
    updateProduct,
    deleteProduct,
})(ProductsList)
